import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { confirmDialog } from 'primereact/confirmdialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import LaborDropdown from '../../../../../../../components/common/LaborDropdown';
import WarrantyPaymentTypeRadio from '../../../../../../../components/common/WarrantyPaymentTypeRadio';
import { useGetWarrantyClaimsByIdQuery, useGetWarrantyClaimsLaborByIdQuery, useLazyCalculateLaborCostQuery } from '../../../../../../../api';
import { selectSiteUseId } from '../../../../../../../store/slices/userParamsSlice';
import toastService from '../../../../../../../utils/toastService';

const LaborFormDialog = ({ disabledStatuses, claimId, warrType, dialogVisible, hideDialog, onSave, onDelete, editingPart = null, authorizationCheck, onUpdateSuccess, packageId }) => {
    const { th, getCurrentLanguageId, getCurrentLocale } = useLanguage();
    const debounceTimerRef = useRef(null);

    // Fetch warranty claim data to get chassisNo
    const { data: warrantyClaimData } = useGetWarrantyClaimsByIdQuery({ id: claimId, languageId: getCurrentLanguageId() }, { skip: !claimId });

    // Get warranty claims labor by ID when editing
    const { data: warrantyClaimsLaborById, isLoading: isLoadingLabor, refetch: refetchLaborById } = useGetWarrantyClaimsLaborByIdQuery({ id: editingPart?.id, LanguageId: getCurrentLanguageId() }, { skip: !editingPart?.id });

    // Lazy query for calculating labor cost from backend
    const [calculateLaborCost, { isFetching: isCalculating }] = useLazyCalculateLaborCostQuery();

    const [formData, setFormData] = useState({
        warrClaimId: Number(claimId),
        laborId: null,
        description: '',
        warrantyType: null,
        laborTime: 0,
        hourlyRate: null,
        quantity: 0
    });

    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        // Dialog kapalıysa bir şey yapma
        if (!dialogVisible) return;

        setSubmitted(false);

        if (editingPart) {
            // Eğer getById verisi yükleniyorsa, bekle (data hazır olunca tekrar tetiklenecek)
            if (isLoadingLabor) return;

            // Use getById data if available, otherwise fall back to editingPart data
            const laborData = warrantyClaimsLaborById?.data || editingPart;

            setFormData({
                id: laborData.id,
                totalAmount: laborData.laborPrice,
                acceptedAmount: laborData.acceptedAmount,
                warrClaimId: laborData.warrClaimId || Number(claimId),
                laborId: laborData.laborId,
                description: laborData.description || '',
                warrantyType: laborData.warrantyType,
                laborTime: laborData.laborTime,
                hourlyRate: laborData.dealerWarrHourlyRate,
                quantity: laborData.quantity ?? editingPart.quantity ?? 1
            });
        } else {
            setFormData({
                warrClaimId: Number(claimId),
                laborId: null,
                description: '',
                warrantyType: null,
                laborTime: 1,
                hourlyRate: null,
                quantity: 1
            });
        }
    }, [editingPart, dialogVisible, claimId, warrantyClaimsLaborById, isLoadingLabor]);

    const handleChange = (value, key) => {
        setFormData((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    // Debounce ile backend'den maliyet hesaplama
    const calculateLaborCostFromApi = useCallback(
        (laborTime, quantity) => {
            // Önceki timer'ı temizle
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // Gerekli değerler yoksa API çağrısı yapma
            const laborId = typeof formData.laborId === 'object' ? formData.laborId?.id : formData.laborId;
            if (!laborId || !formData.warrantyType || !claimId) {
                return;
            }

            // 600ms debounce ile API çağrısı
            debounceTimerRef.current = setTimeout(async () => {
                try {
                    const result = await calculateLaborCost({
                        WarrClaimId: Number(claimId),
                        LaborId: laborId,
                        LaborCode: formData.laborId?.code || '',
                        WarrantyType: Number(formData.warrantyType),
                        LaborTime: laborTime,
                        Quantity: quantity
                    }).unwrap();

                    if (result) {
                        setFormData((prev) => ({
                            ...prev,
                            totalAmount: result.totalAmount,
                            acceptedAmount: result.acceptedAmount
                        }));
                    }
                } catch (error) {
                    console.error('Maliyet hesaplama hatası:', error);
                    toastService.error(th('warrantyClaims.detail.labor.calculationError'));
                }
            }, 600);
        },
        [calculateLaborCost, claimId, formData.laborId, formData.warrantyType, th]
    );

    // Component unmount olduğunda timer'ı temizle
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const handleLaborTimeChange = (value) => {
        const newLaborTime = value || 0;

        setFormData((prev) => ({
            ...prev,
            laborTime: newLaborTime
        }));

        // Sadece editingPart varken API'den hesapla
        if (editingPart) {
            calculateLaborCostFromApi(newLaborTime, formData.quantity);
        }
    };

    const handleQuantityChange = (value) => {
        const newQuantity = Math.floor(value) || 1;

        setFormData((prev) => ({
            ...prev,
            quantity: newQuantity
        }));

        // Sadece editingPart varken API'den hesapla
        if (editingPart) {
            calculateLaborCostFromApi(formData.laborTime, newQuantity);
        }
    };

    const handleSave = async () => {
        setSubmitted(true);

        const isValid = formData.laborId && formData.warrantyType && formData.warrClaimId;
        if (!isValid) return;
        const payload = editingPart
            ? {
                  id: formData.id,
                  warrClaimId: formData.warrClaimId,
                  laborId: formData.laborId.id,
                  description: formData.description,
                  warrantyType: Number(formData.warrantyType),
                  acceptedAmount: formData.acceptedAmount || 0,
                  laborTime: formData.laborTime || 0,
                  quantity: Math.floor(formData.quantity) || 1,
                  isActive: 1
              }
            : {
                  warrClaimId: formData.warrClaimId,
                  laborId: formData.laborId.id,
                  description: formData.description,
                  warrantyType: Number(formData.warrantyType),
                  laborTime: formData.laborTime || 1,
                  quantity: Math.floor(formData.quantity) || 1
              };

        const result = await onSave(payload);

        // Eğer update işlemi başarılıysa ve editingPart varsa, getById endpoint'ini çağır
        if (result && editingPart && onUpdateSuccess) {
            await refetchLaborById();
            onUpdateSuccess();
        }
    };

    const handleDelete = () => {
        confirmDialog({
            message: th('warrantyClaims.detail.labor.deleteConfirm'),
            header: th('warrantyClaims.detail.labor.deleteHeader'),
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                if (onDelete && editingPart) {
                    onDelete(editingPart);
                }
            },
            rejectClassName: 'p-button-outlined'
        });
    };

    const isPackageIdValid = packageId != null && packageId !== 0;
    const isDeleteDisabled = disabledStatuses || isPackageIdValid || isCalculating;
    const isSaveDisabled = disabledStatuses || isCalculating || (editingPart && isPackageIdValid);

    const footer = (
        <div className="flex justify-content-between items-center">
            {editingPart && <Button label={th('warrantyClaims.detail.labor.delete')} icon="pi pi-trash" className="p-button-danger" onClick={handleDelete} disabled={isDeleteDisabled} />}
            <div className="flex gap-2 justify-content-end w-full">
                <Button label={th('warrantyClaims.detail.labor.cancel')} icon="pi pi-times" onClick={hideDialog} outlined disabled={isCalculating} />
                <Button disabled={isSaveDisabled} label={editingPart ? th('warrantyClaims.detail.labor.update') : th('warrantyClaims.detail.labor.create')} icon={isCalculating ? 'pi pi-spin pi-spinner' : 'pi pi-check'} onClick={handleSave} autoFocus />
            </div>
        </div>
    );

    return (
        <Dialog visible={dialogVisible && !isLoadingLabor} style={{ width: '40vw' }} header={th('warrantyClaims.detail.labor.dialogTitle')} modal className="p-fluid" footer={footer} onHide={hideDialog}>
            <div className="field mb-2">
                <label className="font-bold block mb-2">{th('warrantyClaims.detail.labor.laborCatalog')} *</label>
                <LaborDropdown
                    chassisNo={warrantyClaimData?.chassisNo || ''}
                    claimId={claimId}
                    disabled={disabledStatuses}
                    value={formData.laborId}
                    onChange={(value) => {
                        handleChange(value, 'laborId');
                        
                        if (!editingPart && value?.laborTime) {
                            handleChange(value.laborTime, 'laborTime');
                        }
                    }}
                    placeholder={th('warrantyClaims.detail.labor.laborSelectPlaceholder')}
                    className={submitted && !formData.laborId ? 'p-invalid' : ''}
                />
                {submitted && !formData.laborId && <small className="p-error">{th('warrantyClaims.detail.labor.required')}</small>}
            </div>

            {!editingPart && (
                <>
                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.labor.laborTime')}</label>
                        <InputNumber
                            value={formData.laborTime || 1}
                            onChange={(e) => handleChange(e.value || 1, 'laborTime')}
                            mode="decimal"
                            locale={getCurrentLocale()}
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            min={0}
                            step={0.01}
                            disabled={disabledStatuses}
                        />
                    </div>
                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.labor.quantity')}</label>
                        <InputNumber
                            value={formData.quantity || 1}
                            onChange={(e) => handleChange(e.value || 1, 'quantity')}
                            mode="decimal"
                            minFractionDigits={0}
                            maxFractionDigits={0}
                            min={1}
                            step={1}
                            disabled={disabledStatuses}
                        />
                    </div>
                </>
            )}

            {editingPart && (
                <>
                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.labor.laborTime')}</label>
                        <div className="p-inputgroup">
                            <InputNumber
                                value={formData.laborTime || 0}
                                onChange={(e) => handleLaborTimeChange(e.value)}
                                mode="decimal"
                                locale={getCurrentLocale()}
                                minFractionDigits={0}
                                maxFractionDigits={2}
                                min={0}
                                step={0.01}
                                disabled={disabledStatuses || isCalculating || warrType === 'B'}
                            />
                            {isCalculating && <span className="p-inputgroup-addon"><i className="pi pi-spin pi-spinner" /></span>}
                        </div>
                    </div>

                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.labor.quantity')}</label>
                        <div className="p-inputgroup">
                            <InputNumber
                                value={formData.quantity || 1}
                                onChange={(e) => handleQuantityChange(e.value)}
                                mode="decimal"
                                minFractionDigits={0}
                                maxFractionDigits={0}
                                min={1}
                                step={1}
                                disabled={disabledStatuses || isCalculating  || warrType === 'B'}
                            />
                            {isCalculating && <span className="p-inputgroup-addon"><i className="pi pi-spin pi-spinner" /></span>}
                        </div>
                    </div>

                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.labor.totalAmount')}</label>
                        <div className="p-inputgroup">
                            <InputText value={isCalculating ? th('warrantyClaims.detail.labor.calculatingCost') : (formData.totalAmount || '')} disabled />
                            {isCalculating && <span className="p-inputgroup-addon"><i className="pi pi-spin pi-spinner" /></span>}
                        </div>
                    </div>

                    <div className="field mb-2">
                        <label>{th('warrantyClaims.detail.partTable.acceptedAmount')}</label>
                        <div className="p-inputgroup">
                            {isCalculating ? (
                                <InputText value={th('warrantyClaims.detail.labor.calculatingCost')} disabled />
                            ) : (
                                <InputNumber value={formData.acceptedAmount || 0} onChange={(e) => handleChange(e.value || 0, 'acceptedAmount')} mode="decimal" locale={getCurrentLocale()} minFractionDigits={0} maxFractionDigits={2} step={0.01} disabled={!authorizationCheck} />
                            )}
                            {isCalculating && <span className="p-inputgroup-addon"><i className="pi pi-spin pi-spinner" /></span>}
                        </div>
                    </div>
                </>
            )}

            <div className="field mb-2">
                <label className="font-bold block mb-2">{th('warrantyClaims.detail.partDialog.warrantyType')} *</label>
                <WarrantyPaymentTypeRadio
                    value={formData.warrantyType}
                    onChange={(value) => handleChange(value, 'warrantyType')}
                    className={submitted && !formData.warrantyType ? 'p-invalid' : ''}
                    disabled={disabledStatuses || warrType === 'B'  }
                    excludeOptions={warrType === 'MSG' ? ['2'] : []}
                    warrType={warrType}
                />
                {submitted && !formData.warrantyType && <small className="p-error">{th('warrantyClaims.detail.labor.required')}</small>}
            </div>

            <div className="field mb-2">
                <label className="font-bold block mb-2">{th('warrantyClaims.detail.labor.description')}</label>
                <InputText disabled={disabledStatuses} value={formData.description} onChange={(e) => handleChange(e.target.value, 'description')} />
            </div>
        </Dialog>
    );
};

export default LaborFormDialog;
